import { useEffect, useRef, useState } from "react";
import { ForgotPassword3Fields, SmartErrorAlert } from "../../common/components/SmartErrorAlert";
import { InvalidField } from "../../common/functions/errorAlertContent/getAlertContent";
import translate from "../../common/functions/translate";
import { ForgotPasswordErrorsResponse, useForgotPasswordApi } from "../../common/hooks/useForgotPasswordApi";
import { Buttons, FieldErrState, Step } from "./ForgotPassword";
import { NewPassword } from '../../common/components/NewPassword/NewPassword';
import { PasswordErrState } from "../../common/components/NewPassword/passwordSchema";
import { DateObject } from '../../common/objects/models/date.field.value';
import { useConversion } from "../../common/hooks/use.analytics";
import { useHistory } from "react-router";
import { UpdateAccountResponse, useUpdateAccountApi } from "../../common/hooks/useUpdateAccountApi";
import ErrorAlert from "../../common/components/ErrorAlert";
import { handleUpdateAccountResponseCodes } from "../../common/functions/handleUpdateAccountResponse";
import { UpdateAccountErrorKeys, UpdateAccountErrorListKeys, UpdateAccountHeaderErrorKeys } from "../../login/components/UpdateAccountSection";
import { useUpdateAccountAnalytics } from "../../common/hooks/useUpdateAccountAnalytics";
import { useUpdateAccountFieldErrorMessageList } from "../../common/hooks/useUpdateAccountFieldError";
import { useUpdateAccountSetMessage } from "../../common/hooks/useUpdateAccountSetMessage";
import { useUpdateAccountSetHeading } from "../../common/hooks/useUpdateAccountSetHeading";

interface CreateNewPasswordProps {
    combinedWithUsername: boolean
    conversion: ReturnType<typeof useConversion> | null 
    formStart: () => void
    setStep: React.Dispatch<React.SetStateAction<number>>
    medicareNumber: string
    username: string
    lastName: string
    suffix: string
    dateOfBirth: DateObject
    secretQuestion: string
    secretAnswer: string
    mfaReset?: {
        username: string;
        password: string;
    }
    adminPasswordReset?: boolean
}

export const CreateNewPassword = (props: CreateNewPasswordProps) => {
    const { 
        conversion, 
        formStart, 
        setStep, 
        medicareNumber, 
        username, 
        lastName, 
        suffix, 
        dateOfBirth, 
        secretQuestion, 
        secretAnswer, 
        mfaReset,
        adminPasswordReset,
    } = props;

    const [step3ApiErrResponse, setStep3ApiErrResponse] = useState<ForgotPasswordErrorsResponse | null>(null);
    const [passwordRef, setPasswordRef] = useState<HTMLElement | null>(null);
    const [confirmPasswordRef, setConfirmPasswordRef] = useState<HTMLElement | null>(null);
    const [submitPasswordLoading, setSubmitPasswordLoading] = useState(false);
    const [step3InvalidFields, setStep3InvalidFields] = useState<InvalidField<ForgotPassword3Fields>[]>([]);
    const [passwordErrState, setPasswordErrState] = useState<FieldErrState | 'alreadyUsed'>(null);
    const [confirmPasswordErrState, setConfirmPasswordErrState] = useState<FieldErrState>(null);
    const [password, setPassword] = useState('');
    const [submit3Attempted, setSubmit3Attempted] = useState(false);
    const [passwordErrs, setPasswordErrs] = useState<Set<PasswordErrState>>(new Set());
    const [apiErrorHeaderKey, setApiErrorHeaderKey] = useState<UpdateAccountHeaderErrorKeys | null>(null);
    const [apiErrorKey, setApiErrorKey] = useState<UpdateAccountErrorKeys | null>(null);
    const [apiErrorListKey, setApiErrorListKey] = useState<UpdateAccountErrorListKeys| null>(null);
    const [apiErrorMessageList, setApiErrorMessageList] = useState<JSX.Element[]>([]);
    const [analyticsKey, setAnalyticsKey] = useState<UpdateAccountErrorKeys | UpdateAccountErrorListKeys | "unknownError">();
    const [counter, setCounter] = useState(0);
    const [alreadyUsed, setAlreadyUsed] = useState(false);
    const [apiErrorMessage, setApiErrorMessage] = useState<string | null>();
    const [apiErrorHeading, setApiErrorHeading] = useState<string>();

    const errorContainerRef = useRef<HTMLDivElement | null>(null);
    const forgotPasswordApi = useForgotPasswordApi();
    const header = translate('create_new_password');
    const history = useHistory();
    const updateAccountApi = useUpdateAccountApi();

    useUpdateAccountFieldErrorMessageList({passwordRef, header, apiErrorListKey, setApiErrorMessageList, apiErrorMessageList});
    useUpdateAccountAnalytics({analyticsKey, conversion});
    useUpdateAccountSetMessage({setApiErrorMessage, apiErrorKey, newPasswordRef: passwordRef});
    useUpdateAccountSetHeading({setApiErrorHeading, apiErrorHeaderKey, apiErrorMessage});

    /**
     * The NewPassword component handles its own errors, so we will set the passwordErrState
     * primarily based on the error state coming from the the component and not the value of
     * the password itself
     */
    const lastPassword = useRef('');
    const lastNumErrs = useRef(-1);
    useEffect(() => {
        // only run when password or number of errors changes, to prevent prematurely overriding
        // state changes set in onStep3InvalidFields()
        if (passwordErrs.size === lastNumErrs.current && password === lastPassword.current) {
            return;
        }
        lastNumErrs.current = passwordErrs.size;
        lastPassword.current = password;

        const errState: FieldErrState = password.length ? 'invalid' : 'empty';
        setPasswordErrState(passwordErrs.size ? errState : null);
    }, [passwordErrs, password]);

    const onStep3InvalidFields = (fields: InvalidField<ForgotPassword3Fields>[]) => {
        fields.forEach(field => {
            if (field.name === 'newPassword') {
                setPasswordErrState(field.type);
            }
        });
    }

    const onPasswordChange = (newPassword: string) => {
        formStart();
        setPassword(newPassword);
    };

    const onConfirmPasswordChange = () => {
        formStart();
    };

    const prev = () => {
        if (mfaReset) {
            history.goBack();
            return;
        }
        setStep(i => i - 1);
    };

    const next = () => {
        setStep(i => i + 1);
    };

    const submitNewPassword = async () => {
        return forgotPasswordApi.resetPassword({
            mbi: medicareNumber,
            username,
            lastName,
            suffix,
            dateOfBirth,
            secretQuestion,
            secretAnswer,
            password
        });
    };

    const handleCreateNew = async () => {
        try {
            await submitNewPassword();
            conversion && conversion.success();
            next();
        } catch (e: any) {
            setStep3ApiErrResponse(e);
        } finally {
            setSubmitPasswordLoading(false);
        }
    }

    const handleUpdateAccount = async () => {
        if (!mfaReset) {
            return;
        }

        const headers = {
            headers: {
                'accept': '*/*',
                'content-type': 'application/json'
            }
        };
        const updateAccountArgs = {
            updates: {
                password
            },
            headers
        }
        const updateAccountResponse: UpdateAccountResponse = await updateAccountApi.updateAccount(updateAccountArgs);
        // updateAccountResponse returns undefined if already in flight
        if (!updateAccountResponse) {
            return;
        }

        if (updateAccountResponse.code === 204) {
            conversion && conversion.success();
            next();
        }

        setCounter(prev => prev + 1);

        const updateResponseArgs = {
            updateAccountResponse,
            setApiErrorHeaderKey,
            setApiErrorKey,
            setAnalyticsKey,
            setApiErrorListKey,
            setAlreadyUsed,
            // newPasswordErrorSetter: newPasswordError.set,
            // confirmNewPasswordErrorSetter: confirmNewPasswordError.set,
            errorContainerRef,
            conversion, 
            showPageCallback: () => setStep(3),
            count: counter + 1,
        }
        handleUpdateAccountResponseCodes(updateResponseArgs);
        setSubmitPasswordLoading(false);
    }

    const resetPassword = async () => {
        setSubmit3Attempted(true);
        if (submitPasswordLoading) {
            return;
        }
        conversion && conversion.attempt();

        const newInvalidFields: InvalidField<ForgotPassword3Fields>[] = [];
        if (passwordErrState) {
            newInvalidFields.push({ name: 'newPassword', type: passwordErrState });
        }
        if (confirmPasswordErrState) {
            newInvalidFields.push({ name: 'confirmPassword', type: confirmPasswordErrState });
        }
        setStep3InvalidFields(newInvalidFields);

        if (newInvalidFields.length) {
            return;
        }

        setSubmitPasswordLoading(true);
        setStep3ApiErrResponse(null);

        if (mfaReset) {
            handleUpdateAccount();
            return;
        }

        handleCreateNew();
    };

    const getTitle = () => {
        if (mfaReset) {
            return translate("protect_your_account");
        }
        if (adminPasswordReset) {
            return translate("update_your_password");
        }
    }

    const btnTxt = adminPasswordReset ? translate('update_password') : translate('reset_password');

    return ( 
        <Step 
            stepNum={3} 
            mfaPasswordReset={mfaReset ? true : undefined} 
            title={getTitle()} 
            stepTitle={translate('create_new_password')} 
            combinedWithUsername={props.combinedWithUsername} 
            allFieldsRequired
        >
            <SmartErrorAlert
                name="ForgotPassword3"
                conversion={conversion}
                apiResponse={step3ApiErrResponse}
                newPasswordRef={passwordRef}
                confirmPasswordRef={confirmPasswordRef}
                invalidFields={step3InvalidFields}
                onInvalidField={onStep3InvalidFields}
                parentHeading={header}
            />
            <div ref={errorContainerRef} tabIndex={-1}>
                {(apiErrorMessage || apiErrorMessageList.length > 0) &&
                    <ErrorAlert
                        className="ds-u-margin-bottom--3"
                        heading={apiErrorHeading}
                        errorMessage={apiErrorMessage}
                        validationMessages={apiErrorMessageList} />
                }
            </div>
            <NewPassword
                component={'forgotPassword'}
                onPasswordChange={onPasswordChange}
                onConfirmPasswordChange={onConfirmPasswordChange}
                onPasswordErrorTypes={setPasswordErrs}
                onConfirmPasswordError={hasErr => setConfirmPasswordErrState(hasErr ? 'invalid' : null)}
                passwordLabel={translate('enter_new_password')}
                confirmPasswordLabel={translate('confirm_new_password')}
                passwordRef={setPasswordRef}
                confirmPasswordRef={setConfirmPasswordRef}
                submitAttempted={submit3Attempted}
                apiErrorKey={passwordErrState === 'invalid' ? 'passwordGuidelines' : undefined}
                showAlreadyUsedError={alreadyUsed}
            />
            <Buttons
                backText={translate('back')}
                forwardText={btnTxt}
                isLoading={submitPasswordLoading}
                onBackClick={prev}
                onForwardClick={resetPassword}
            />
        </Step>
    );
}